import {
  DeleteMode,
  GroupResponseV1,
  GroupType,
  HouseholdMemberResponseV1,
  HouseholdRelationship,
  PermissionEffect,
  PermissionObjectNamespace,
  PermissionResponseV1,
  RoleScopeResponseV1,
  UserResponseV1
} from '@access-management/access-management';
import { DeleteEntityDialog } from '@access-management/delete-entity-dialog/delete-entity-dialog';
import { InvitationResponseV1, InvitationStatus } from '@access-management/invitation';
import { MembershipExpirationPanel } from '@access-management/membership-expiration/membership-expiration-panel';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AdminUserAllocationResponseV1 } from '@entitlements/entitlements';
import { AccessManagementService } from '@services/access-management';
import { EntitlementsService } from '@services/entitlements';
import { InvitationService } from '@services/invitation';
import { UsersService } from '@services/users';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, firstValueFrom, forkJoin, map, of, throwError } from 'rxjs';

import { isWildcardScope, scopeAllows } from '@core/models/scope';
import { UserProfileV1 } from '@core/models/users';
import { environment } from '@src/environments/environment';

import { DailyResultsPanel } from './daily-results-panel/daily-results-panel';

/**
 * What a household's in-force denies do to one bundle scope.
 *
 * Three states because a binary one cannot describe the middle: an Admin denied Orders.Guests keeps
 * Orders.Create and loses the wildcard, which is neither cancelled nor untouched.
 * `removed` names the leaves that went, which is the only thing that makes a narrowing legible.
 */
type BundleScopeFold = { state: 'none' } | { state: 'restricted' | 'cancelled'; removed: string[] };

@Component({
  selector: 'app-user-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ButtonModule,
    CardModule,
    MessageModule,
    ProgressSpinnerModule,
    TableModule,
    TagModule,
    TooltipModule,
    DeleteEntityDialog,
    MembershipExpirationPanel,
    DailyResultsPanel
  ],
  templateUrl: './user-detail-page.html',
  styleUrl: './user-detail-page.scss'
})
export class UserDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly accessManagementService = inject(AccessManagementService);
  private readonly invitationService = inject(InvitationService);
  private readonly usersService = inject(UsersService);
  private readonly entitlementsService = inject(EntitlementsService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly dailyResultsPanel = viewChild(DailyResultsPanel);

  readonly userId = toSignal(this.route.paramMap.pipe(map((p) => p.get('userId'))), { initialValue: null });

  readonly userResource = rxResource<UserResponseV1 | null, string | null>({
    params: () => this.userId(),
    stream: ({ params }) => {
      if (!params) return of(null);
      return this.accessManagementService.getUserById(params).pipe(
        map((u) => u ?? null),
        catchError((err) => throwError(() => (err instanceof Error ? err : new Error(String(err)))))
      );
    }
  });

  readonly profileResource = this.usersService.createUserProfileResource(() => this.userId());

  readonly invitationsResource = rxResource<InvitationResponseV1[], string | null>({
    params: () => this.userId(),
    stream: ({ params }) => {
      if (!params) return of([]);
      return this.invitationService.getInvitationsForInviter(params);
    }
  });

  readonly entitlementsResource = this.entitlementsService.createUserEntitlementsResource(() => this.userId());

  readonly membershipExpirationEnabled = environment.features.membershipExpirationOverride;

  // `hasValue()` rather than `value()?.allocations`: a resource in its error state THROWS from
  // `value()`, so the page would abort rendering instead of showing the Entitlements card's own
  // failure message.
  readonly membershipIds = computed(() => {
    const entitlements = this.entitlementsResource.hasValue() ? this.entitlementsResource.value() : null;
    return [
      ...new Set(
        (entitlements?.allocations ?? [])
          .map((allocation) => allocation.membershipId)
          .filter((id): id is string => !!id)
      )
    ];
  });

  readonly householdResource = rxResource<HouseholdMemberResponseV1[], string | null>({
    params: () => this.userId(),
    stream: ({ params }) => {
      if (!params) return of([]);
      return this.accessManagementService.getUserHousehold(params);
    }
  });

  readonly groupsResource = rxResource<GroupResponseV1[], string | null>({
    params: () => this.userId(),
    stream: ({ params }) => {
      if (!params) return of([]);
      return this.accessManagementService.getUserGroups(params);
    }
  });

  /** Distinct role ids across the user's group memberships, joined for stable resource params. */
  private readonly userRoleIdsKey = computed(() => {
    const userId = this.userId();
    const ids = new Set<string>();
    for (const group of this.groupsResource.value() ?? []) {
      const membership = group.members?.find((m) => m.id === userId);
      if (membership?.roleId) ids.add(membership.roleId);
    }
    return [...ids].sort().join(',');
  });

  readonly roleScopesResource = rxResource<{ roleId: string; scopes: RoleScopeResponseV1[] }[], string>({
    params: () => this.userRoleIdsKey(),
    stream: ({ params }) => {
      const roleIds = params ? params.split(',') : [];
      if (roleIds.length === 0) return of([]);
      return forkJoin(
        roleIds.map((roleId) =>
          this.accessManagementService.getRoleScopes(roleId).pipe(map((scopes) => ({ roleId, scopes })))
        )
      );
    }
  });

  /**
   * Scopes the user holds through their role in each group: one row per membership, named by both.
   *
   * Per membership rather than per role, because an authorization decision is about an object and a
   * row that spans two households cannot carry two decisions. The seeded role ids are shared across
   * every household, so the Admin bundle a user holds in two of them was one row, and a deny of
   * Wallet written over one struck the single shared tag -- reporting a capability as gone in a
   * household where nothing had touched it, with no representation left of the authority still held
   * there. Naming the group on the row is the other half: the same role now appears more than once,
   * and which one a tag belongs to is the whole question.
   */
  readonly roleScopeRows = computed(() => {
    const userId = this.userId();
    const scopesByRole = new Map((this.roleScopesResource.value() ?? []).map((entry) => [entry.roleId, entry.scopes]));

    const rows: {
      key: string;
      groupId: string;
      groupName: string;
      roleId: string;
      roleName: string;
      scopes: RoleScopeResponseV1[];
    }[] = [];

    for (const group of this.groupsResource.value() ?? []) {
      const membership = group.members?.find((m) => m.id === userId);
      if (!membership?.roleId) continue;
      rows.push({
        key: `${group.groupId}|${membership.roleId}`,
        groupId: group.groupId,
        groupName: group.name ?? group.groupId,
        roleId: membership.roleId,
        roleName: membership.roleName ?? membership.roleId,
        scopes: scopesByRole.get(membership.roleId) ?? []
      });
    }

    return rows;
  });

  readonly grantedToResource = rxResource<PermissionResponseV1[], string | null>({
    params: () => this.userId(),
    stream: ({ params }) => {
      if (!params) return of([]);
      return this.accessManagementService.getPermissionsGrantedTo(params);
    }
  });

  readonly grantedByResource = rxResource<PermissionResponseV1[], string | null>({
    params: () => this.userId(),
    stream: ({ params }) => {
      if (!params) return of([]);
      return this.accessManagementService.getPermissionsGrantedBy(params);
    }
  });

  readonly scopesResource = this.accessManagementService.createScopesResource();

  /** The user's row in each of their groups, so the role columns render alongside the group. */
  readonly groupRows = computed(() => {
    const userId = this.userId();
    return (this.groupsResource.value() ?? []).map((group) => {
      const membership = group.members?.find((m) => m.id === userId) ?? null;
      return {
        group,
        isHousehold: group.groupType === GroupType.Household,
        roleName: membership?.roleName ?? null,
        householdRelationship: membership?.householdRelationship ?? null
      };
    });
  });

  /** Best-effort id -> name map from the members of every group the user belongs to. */
  private readonly memberNamesById = computed(() => {
    const names = new Map<string, string>();
    for (const group of this.groupsResource.value() ?? []) {
      for (const member of group.members ?? []) {
        if (member.name) names.set(member.id, member.name);
      }
    }
    return names;
  });

  readonly householdSelf = computed<HouseholdMemberResponseV1 | null>(() => {
    const roster = this.householdResource.value() ?? [];
    return roster.find((m) => m.youtopianId === this.userId()) ?? null;
  });

  readonly guardians = computed<HouseholdMemberResponseV1[]>(() => this.resolveRelated((m) => m.guardianUserIds));

  readonly dependents = computed<HouseholdMemberResponseV1[]>(() => this.resolveRelated((m) => m.dependentUserIds));

  readonly togglingAllocationId = signal<string | null>(null);

  readonly showDeleteDialog = signal(false);
  readonly deleting = signal(false);

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return '—';
    try {
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  }

  formatBool(value: boolean | undefined | null): string {
    if (value === null || value === undefined) return '—';
    return value ? 'Yes' : 'No';
  }

  fullName(profile: UserProfileV1 | null | undefined): string {
    if (!profile) return '—';
    const first = profile.firstName ?? '';
    const last = profile.lastName ?? '';
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    return profile.preferredName || profile.email;
  }

  getInvitationStatusSeverity(status: InvitationStatus): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case InvitationStatus.Pending:
        return 'info';
      case InvitationStatus.Accepted:
        return 'success';
      case InvitationStatus.Declined:
      case InvitationStatus.Cancelled:
        return 'warn';
      case InvitationStatus.Expired:
        return 'danger';
      case InvitationStatus.PendingPurchase:
      default:
        return 'secondary';
    }
  }

  getAllocationStateSeverity(allocation: AdminUserAllocationResponseV1): 'success' | 'warn' | 'danger' {
    if (allocation.isDeactivated) return 'danger';
    if (allocation.isExpired) return 'warn';
    return 'success';
  }

  getAllocationStateLabel(allocation: AdminUserAllocationResponseV1): string {
    if (allocation.isDeactivated) return 'Deactivated';
    if (allocation.isExpired) return 'Expired';
    return 'Active';
  }

  onDeactivateAllocation(allocation: AdminUserAllocationResponseV1): void {
    this.confirmationService.confirm({
      message: `Deactivate allocation for ${allocation.featureName || allocation.featureCode}? The user will no longer be able to consume this entitlement.`,
      header: 'Confirm Deactivation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.toggleAllocationLifecycle(allocation.featureAllocationId, 'deactivate')
    });
  }

  onReactivateAllocation(allocation: AdminUserAllocationResponseV1): void {
    this.confirmationService.confirm({
      message: `Reactivate allocation for ${allocation.featureName || allocation.featureCode}? The user will be able to consume this entitlement again.`,
      header: 'Confirm Reactivation',
      icon: 'pi pi-check-circle',
      accept: () => this.toggleAllocationLifecycle(allocation.featureAllocationId, 'reactivate')
    });
  }

  scopeLabel(scopeId: string): string {
    const scope = (this.scopesResource.value() ?? []).find((s) => s.scopeId === scopeId);
    if (!scope) return scopeId;
    const path = scope.fullPath;
    return scope.namespace ? `${scope.namespace}/${path}` : path;
  }

  /**
   * The in-force deny that cancels this bundle scope, or null.
   *
   * The two sections below are the inputs to the decision, not the decision, and an operator reading
   * "can this member edit the household plan?" stops at `Plan.*` in the role's tag row. The deny two
   * sections down is what the gate applies. Marking the tag puts the answer where the question is
   * asked, and does it from rows the page already holds rather than a read of its own.
   *
   * Matched through scopeAllows, so a deny of a wildcard cancels the leaves under it the way the
   * engine's own subtraction does. A revoked or expired row cancels nothing.
   */
  /**
   * The group-namespace concrete paths a wildcard can stand for, from the catalog this page loads.
   *
   * What makes the subtraction bite: removing Orders.Create from a set holding Orders.* removes
   * nothing, because the wildcard goes on answering the request. The leaves say which concrete
   * authorities it stood for.
   *
   * Read from the scope catalog rather than from a copy of the engine's static one, which is the
   * closest this console can get. They differ in one case: an admin-authored concrete scope is in
   * here and is not in the engine's leaf set, so a deny of one is subtracted here and restored there.
   * That gap is the engine's to close -- EffectiveScopes.Compute has to decide whether an authored
   * path joins the fold -- and showing the narrowing the operator asked for is the better side of it
   * to be wrong on.
   */
  private readonly groupLeafScopeNames = computed(() =>
    (this.scopesResource.value() ?? [])
      .filter((scope) => scope.namespace === PermissionObjectNamespace.Group && !isWildcardScope(scope.fullPath))
      .map((scope) => scope.fullPath)
  );

  /**
   * What the household's in-force denies do to one bundle scope: nothing, narrow it, or end it.
   *
   * The engine's own arithmetic rather than a yes/no match against the literal string. Asking only
   * whether a deny covers the tag's own path answered false for Plan.* under a Plan.Edit deny --
   * scopeAllows('Plan.Edit', 'Plan.*') is false, because a leaf never answers its own wildcard -- and
   * the console left the tag active while the household-wide plan gate, which asks for Plan.*
   * precisely so a delegated Plan.Edit cannot reach it, refuses. AME expands the wildcard, subtracts
   * the denied leaf, and drops Plan.* because Edit is its only leaf.
   *
   * Three states, because a binary one cannot describe the middle: an Admin denied Orders.Guests
   * keeps Orders.Create and loses the wildcard, which is neither "cancelled" nor "untouched".
   */
  private foldBundleScope(scopeName: string, groupId: string): BundleScopeFold {
    const untouched = { state: 'none' as const };

    const denied = (this.grantedToResource.value() ?? [])
      .filter(
        (permission) =>
          permission.effect === PermissionEffect.Deny &&
          this.getPermissionStatus(permission) === 'Active' &&
          // Over the household this bundle is held in, and over the household rather than over the
          // person: a user-object deny takes away nothing a group bundle confers, and a deny in
          // another household takes away nothing here.
          permission.objectNamespace === PermissionObjectNamespace.Group &&
          permission.objectId === groupId
      )
      .map((permission) => this.scopeNameOf(permission.scopeId) ?? '');

    if (denied.length === 0) return untouched;

    const coveredByADeny = (path: string) => denied.some((deny) => scopeAllows(deny, path));

    // A deny covering the tag itself ends it, wildcard or leaf. For a wildcard that is a deny of the
    // wildcard by name or of something above it, and it has to be taken before the leaf arithmetic:
    // a wildcard the catalog knows no leaves for expands to nothing, loses none of it, and would
    // otherwise come back whole from the household's most explicit possible refusal.
    if (coveredByADeny(scopeName)) return { state: 'cancelled', removed: [scopeName] };
    if (!isWildcardScope(scopeName)) return untouched;

    const leaves = this.groupLeafScopeNames().filter((leaf) => scopeAllows(scopeName, leaf));
    const removed = leaves.filter(coveredByADeny);
    if (removed.length === 0) return untouched;

    // Every leaf gone means the wildcard has nothing left to stand for. Some gone means the leaves
    // that survive are what the member keeps, and the wildcard itself -- which the household-wide
    // gates ask for -- is not among them.
    return { state: removed.length === leaves.length ? 'cancelled' : 'restricted', removed };
  }

  /**
   * Every bundle tag's state, resolved once per dependency change instead of once per binding.
   *
   * Each tag binds the question three times (severity, class, tooltip) and the tooltip asks it a
   * fourth time internally, and every ask ran a linear find over the scope catalogue plus one more per
   * candidate permission. On a page of PrimeNG tables and tooltips, which tick change detection
   * freely, a 60-entry catalogue against 10 bundle scopes and 20 permission rows came to roughly
   * 38,000 array scans per cycle. Keyed on the GROUP as well as the role, because the rows are now
   * one per membership and the same role in two households holds two separate answers. Reading one
   * map also removes the chance of the three bindings disagreeing with each other.
   */
  private readonly bundleTagCancellations = computed(() => {
    const state = new Map<string, { state: 'restricted' | 'cancelled'; tooltip: string }>();

    for (const row of this.roleScopeRows()) {
      for (const roleScope of row.scopes) {
        const scopeName = this.scopeNameOf(roleScope.scopeId);
        if (!scopeName) continue;

        const fold = this.foldBundleScope(scopeName, row.groupId);
        if (fold.state === 'none') continue;

        state.set(`${row.groupId}|${row.roleId}|${roleScope.scopeId}`, {
          state: fold.state,
          tooltip: this.bundleFoldTooltip(scopeName, fold, row.groupName)
        });
      }
    }

    return state;
  });

  private bundleFoldTooltip(
    scopeName: string,
    fold: Extract<BundleScopeFold, { state: 'restricted' | 'cancelled' }>,
    groupName: string
  ): string {
    return fold.state === 'cancelled'
      ? `An in-force deny removes ${scopeName} in ${groupName}.`
      : `Narrowed in ${groupName}: an in-force deny removes ${fold.removed.join(', ')}, ` +
          `so ${scopeName} no longer answers as a whole.`;
  }

  /** Whether an in-force deny removes this bundle tag outright, as one map lookup. */
  bundleTagCancelled(groupId: string, roleId: string, scopeId: string): boolean {
    return this.bundleTagCancellations().get(`${groupId}|${roleId}|${scopeId}`)?.state === 'cancelled';
  }

  /** Whether a deny narrows this bundle tag without removing it: some leaves survive, the wildcard does not. */
  bundleTagRestricted(groupId: string, roleId: string, scopeId: string): boolean {
    return this.bundleTagCancellations().get(`${groupId}|${roleId}|${scopeId}`)?.state === 'restricted';
  }

  /** The state's explanation, or empty when nothing touches the tag. */
  bundleTagTooltip(groupId: string, roleId: string, scopeId: string): string {
    return this.bundleTagCancellations().get(`${groupId}|${roleId}|${scopeId}`)?.tooltip ?? '';
  }

  /**
   * The in-force deny that removes this bundle scope over one household, or null.
   *
   * The ROW, where {@link bundleTagCancelled} is the state: an operator asking which deny to revoke
   * needs the row. Answers only for a scope the fold actually removes, so it cannot name a deny that
   * merely narrows a wildcard -- revoking that one would give back the whole family.
   */
  cancellingDeny(scopeId: string, groupId: string): PermissionResponseV1 | null {
    const scopeName = this.scopeNameOf(scopeId);
    if (!scopeName || !groupId) return null;
    if (this.foldBundleScope(scopeName, groupId).state !== 'cancelled') return null;

    return (
      (this.grantedToResource.value() ?? []).find(
        (permission) =>
          permission.effect === PermissionEffect.Deny &&
          this.getPermissionStatus(permission) === 'Active' &&
          permission.objectNamespace === PermissionObjectNamespace.Group &&
          permission.objectId === groupId &&
          scopeAllows(this.scopeNameOf(permission.scopeId) ?? '', scopeName)
      ) ?? null
    );
  }

  /**
   * The in-force deny that cancels an Allow override row, if one does.
   *
   * The same question the bundle tags ask, asked about a row rather than about a role: an Allow and a
   * Deny can both be live over the same object, the engine subtracts the deny last, and a table showing
   * the Allow as Active with nothing beside it reports the opposite of what the gate does. Matched over
   * the row's OWN object, and never against itself.
   */
  cancellingDenyForOverride(permission: PermissionResponseV1): PermissionResponseV1 | null {
    if (permission.effect !== PermissionEffect.Allow) return null;
    if (this.getPermissionStatus(permission) !== 'Active') return null;

    const scopeName = this.scopeNameOf(permission.scopeId);
    if (!scopeName) return null;

    return (
      (this.grantedToResource.value() ?? []).find(
        (candidate) =>
          candidate.permissionId !== permission.permissionId &&
          candidate.effect === PermissionEffect.Deny &&
          this.getPermissionStatus(candidate) === 'Active' &&
          candidate.objectNamespace === permission.objectNamespace &&
          candidate.objectId === permission.objectId &&
          scopeAllows(this.scopeNameOf(candidate.scopeId) ?? '', scopeName)
      ) ?? null
    );
  }

  cancellingDenyForOverrideTooltip(permission: PermissionResponseV1): string {
    const deny = this.cancellingDenyForOverride(permission);
    if (!deny) return '';
    return `Cancelled by an in-force deny of ${this.scopeNameOf(deny.scopeId) ?? deny.scopeId}.`;
  }

  cancellingDenyTooltip(scopeId: string, groupId: string): string {
    const deny = this.cancellingDeny(scopeId, groupId);
    if (!deny) return '';
    const denied = this.scopeNameOf(deny.scopeId) ?? deny.scopeId;
    return `Cancelled by an in-force deny of ${denied} over ${this.objectLabel(deny)}.`;
  }

  // The bare path, without the namespace prefix scopeLabel renders: scopeAllows compares paths, and
  // "group/Plan.*" would not match "Plan.Edit".
  private scopeNameOf(scopeId: string): string | null {
    return (this.scopesResource.value() ?? []).find((s) => s.scopeId === scopeId)?.fullPath ?? null;
  }

  userLabel(userId: string): string {
    return this.memberNamesById().get(userId) ?? userId;
  }

  /**
   * The object the row was written over. A household and a member are both plausible objects for the
   * same scope and they mean different things, so the console names which rather than leaving the
   * reader to infer it from the scope's namespace.
   */
  objectLabel(permission: PermissionResponseV1): string {
    const { objectNamespace, objectId } = permission;
    if (!objectId) return '—';

    if (objectNamespace === PermissionObjectNamespace.Group) {
      const group = (this.groupsResource.value() ?? []).find((g) => g.groupId === objectId);
      return group?.name ?? `group/${objectId}`;
    }
    if (objectNamespace === PermissionObjectNamespace.User) {
      // Qualified when the name is unknown, the way the other two branches are. memberNamesById is
      // built from members of THIS user's households, so an object outside them -- a dependent in a
      // different household, say -- resolved to a bare guid indistinguishable from an unresolvable
      // group id in the row above it.
      const name = this.memberNamesById().get(objectId);
      return name ?? `user/${objectId}`;
    }
    return objectNamespace ? `${objectNamespace}/${objectId}` : objectId;
  }

  /**
   * Whether the row hands the scope over or takes it away. Rendered on its own rather than folded
   * into the status, because the two are separate questions: a deny that has not been revoked is in
   * force, and a console reading only the status tag would report it as a capability the user holds.
   *
   * A row with no polarity at all reads as `Unknown` rather than as `Allow`. Every row from an
   * engine carrying this change has one (the column is non-nullable), so the case that reaches
   * here is a console running ahead of AccessManagement, which is the deployment skew the merge
   * order plans for. Answering `Allow` there paints every row in both tables green while denies are
   * in force, which is the exact failure the field was added to end; answering `Unknown` says the
   * console cannot tell, which is true.
   */
  getPermissionEffect(permission: PermissionResponseV1): 'Allow' | 'Deny' | 'Unknown' {
    if (permission.effect === PermissionEffect.Deny) return 'Deny';
    if (permission.effect === PermissionEffect.Allow) return 'Allow';
    return 'Unknown';
  }

  getPermissionEffectSeverity(permission: PermissionResponseV1): 'success' | 'danger' | 'secondary' {
    switch (this.getPermissionEffect(permission)) {
      case 'Deny':
        return 'danger';
      case 'Allow':
        return 'success';
      default:
        return 'secondary';
    }
  }

  getPermissionStatus(permission: PermissionResponseV1): 'Active' | 'Revoked' | 'Expired' {
    if (permission.revokedOn) return 'Revoked';
    if (permission.expiresOn && new Date(permission.expiresOn) <= new Date()) return 'Expired';
    return 'Active';
  }

  /**
   * Lifecycle only, in neutral colours. Green-for-active and red-for-revoked reads correctly for a
   * grant and backwards for a deny, where in force is the restrictive state and revoked is the
   * state that gave the capability back, so on a deny row the glance said the opposite of the
   * label beside it. Polarity has its own tag and is the one that carries good-or-bad; this one
   * says only whether the row still applies.
   */
  getPermissionStatusSeverity(permission: PermissionResponseV1): 'info' | 'warn' | 'secondary' {
    switch (this.getPermissionStatus(permission)) {
      case 'Revoked':
        return 'secondary';
      case 'Expired':
        return 'warn';
      default:
        return 'info';
    }
  }

  // No 'info': it was Guardian's, and Guardian is not a relationship any more.
  getHouseholdRelationshipSeverity(relationship: HouseholdRelationship): 'success' | 'warn' | 'secondary' {
    switch (relationship) {
      case HouseholdRelationship.Owner:
        return 'success';
      case HouseholdRelationship.Dependent:
        return 'warn';
      case HouseholdRelationship.Member:
      default:
        return 'secondary';
    }
  }

  refreshAll(): void {
    this.userResource.reload();
    this.profileResource.reload();
    this.invitationsResource.reload();
    this.entitlementsResource.reload();
    this.householdResource.reload();
    this.groupsResource.reload();
    this.roleScopesResource.reload();
    this.grantedToResource.reload();
    this.grantedByResource.reload();
    this.dailyResultsPanel()?.reload();
  }

  openDeleteDialog(): void {
    this.showDeleteDialog.set(true);
  }

  async onConfirmDelete(mode: DeleteMode): Promise<void> {
    const userId = this.userId();
    if (!userId) return;

    this.deleting.set(true);
    try {
      await firstValueFrom(this.accessManagementService.deleteUser(userId, mode));
      this.messageService.add({ severity: 'success', summary: 'User deleted', detail: 'The user was deleted.' });
      this.showDeleteDialog.set(false);
      await this.router.navigate(['/access-management/accounts-users']);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Delete failed',
        detail: 'The user could not be deleted.'
      });
    } finally {
      this.deleting.set(false);
    }
  }

  private resolveRelated(ids: (member: HouseholdMemberResponseV1) => string[]): HouseholdMemberResponseV1[] {
    const roster = this.householdResource.value() ?? [];
    const self = this.householdSelf();
    if (!self) return [];
    return ids(self)
      .map((id) => roster.find((m) => m.youtopianId === id) ?? null)
      .filter((m): m is HouseholdMemberResponseV1 => m !== null);
  }

  private async toggleAllocationLifecycle(allocationId: string, action: 'deactivate' | 'reactivate'): Promise<void> {
    this.togglingAllocationId.set(allocationId);
    try {
      const call =
        action === 'deactivate'
          ? this.entitlementsService.deactivateAllocation(allocationId)
          : this.entitlementsService.reactivateAllocation(allocationId);
      await firstValueFrom(call);
      const label = action === 'deactivate' ? 'deactivated' : 'reactivated';
      this.messageService.add({ severity: 'success', summary: 'Success', detail: `Allocation ${label}.` });
      this.entitlementsResource.reload();
    } catch {
      // EntitlementsService's error handler already surfaced a toast.
    } finally {
      this.togglingAllocationId.set(null);
    }
  }
}
